import { useEffect } from 'react';
import { notificationsApi } from '../services/api';
import { desktop, isDesktop } from '../native/desktop';
import { isNative } from '../native/platform';

/**
 * Tells the backend what this screen shows and whether the person uses it (backend
 * utils/presence.py), every 25 s and on every change:
 *   threads   the chats and tasks it shows ("chat:<id>", "task:<id>"): nothing pings them about
 *             those, and looking at them settles their notifications (utils/notify.py)
 *   front     visible, focused and used in the last IDLE_MS
 *   idle_ms   since the person last used it; the desktop app sends the computer's idle time
 *             (Electron powerMonitor), so work in another program counts too
 *   device    web | desktop | mobile
 * While a browser or the desktop app was used in the last 10 minutes, the phones stay quiet
 * and their pushes wait (Slack: "When I'm not active on desktop"). A screen that leaves the
 * front keeps its threads seen for a minute (Slack holds the phone a minute after the screen
 * locks); a tab that closes says so at once (leaving).
 */
export const BEAT_MS = 25_000;    // the backend forgets a screen after 60 s
export const IDLE_MS = 5 * 60_000;
export const LOCKED_MS = 24 * 3600_000;  // a locked computer counts as long idle
const ACTIVITY = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll'];

const shown = new Map();  // thread -> how many open views show it
let users = 0;            // usePresence / useViewing mounted
let lastActive = Date.now();
let lastSent = '';
let timer = null;

const device = () => {
  if (isNative()) return 'mobile';
  if (isDesktop()) return 'desktop';
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '') ? 'mobile' : 'web';
};

const inFront = () => document.visibilityState === 'visible' && document.hasFocus()
  && Date.now() - lastActive < IDLE_MS;

/** How long the person has not used this screen (the desktop app: the whole computer). */
function idleMs() {
  const own = Date.now() - lastActive;
  const system = isDesktop() ? desktop.systemIdle() : undefined;
  if (!system || typeof system.then !== 'function') return own;
  return system.then((s) => {
    if (!s || typeof s.idle !== 'number') return own;
    return s.state === 'locked' ? LOCKED_MS : Math.min(own, s.idle * 1000);
  }).catch(() => own);
}

function post(body) {
  Promise.resolve(notificationsApi.viewing(body)).catch(() => { lastSent = ''; });
}

function send(force = false, leaving = false) {
  if (leaving) {
    lastSent = '';
    post({ threads: [], leaving: true });  // must arrive while the page goes (keepalive)
    return;
  }
  const front = inFront();
  const threads = [...shown.keys()].sort();
  const key = `${threads.join(',')}|${front}`;
  if (!force && key === lastSent) return;
  lastSent = key;
  const idle = idleMs();
  const body = (ms) => ({ threads, front, idle_ms: Math.max(0, Math.round(ms)), device: device() });
  if (typeof idle === 'number') post(body(idle));
  else idle.then((ms) => post(body(ms)));
}

const beat = () => send(true);
const onActivity = () => {
  const wasIdle = Date.now() - lastActive >= IDLE_MS;
  lastActive = Date.now();
  if (wasIdle) send(true);
};
const onChange = () => send();
const onLeave = () => send(false, true);  // the tab closes or the page reloads

function start() {
  users += 1;
  if (users > 1) return;
  ACTIVITY.forEach((e) => window.addEventListener(e, onActivity, { passive: true, capture: true }));
  document.addEventListener('visibilitychange', onChange);
  window.addEventListener('focus', onChange);
  window.addEventListener('blur', onChange);
  window.addEventListener('pagehide', onLeave);
  timer = window.setInterval(beat, BEAT_MS);
  send(true);
}

function stop() {
  users = Math.max(0, users - 1);
  if (users) return;
  ACTIVITY.forEach((e) => window.removeEventListener(e, onActivity, { capture: true }));
  document.removeEventListener('visibilitychange', onChange);
  window.removeEventListener('focus', onChange);
  window.removeEventListener('blur', onChange);
  window.removeEventListener('pagehide', onLeave);
  window.clearInterval(timer);
  timer = null;
  lastSent = '';
}

/** While mounted (the signed-in app shell, the desktop app's hive), this screen reports. */
export function usePresence(enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;
    start();
    return stop;
  }, [enabled]);
}

/** While the component shows `thread` ("chat:<id>" or "task:<id>"), it is in view. */
export function useViewing(thread) {
  useEffect(() => {
    if (!thread) return undefined;
    shown.set(thread, (shown.get(thread) || 0) + 1);
    start();
    send();
    return () => {
      const left = (shown.get(thread) || 1) - 1;
      if (left > 0) shown.set(thread, left);
      else shown.delete(thread);
      send();
      stop();
    };
  }, [thread]);
}
